"use client";

import { useEffect, useState } from "react";
import { Check, MapPin, Plus, X } from "lucide-react";
import StepMarker from "../StepMarker";
import { Button } from "../Button";

export default function Step4Vendors({
  formData,
  updateFormData,
  eventNeeds,
  onBack,
  onNext,
  onSkip,
  businessData,
}) {
  const selectedNeeds = formData.needs;

  // Automatically skip this step if no needs are selected
  useEffect(() => {
    if (!selectedNeeds || selectedNeeds.length === 0) {
      onNext();
    }
  }, []);

  const [activeNeed, setActiveNeed] = useState(selectedNeeds[0] || "");
  const [showAddNeeds, setShowAddNeeds] = useState(false);
  const [vendors, setVendors] = useState(null);

  useEffect(() => {
    const result = {};

    businessData.forEach((business) => {
      let servicesStr = business.services;
      if (servicesStr.startsWith('"') && servicesStr.endsWith('"')) {
        servicesStr = servicesStr.slice(1, -1);
      }

      const services = servicesStr.split(",").map((service) => service.trim());

      const vendor = {
        id: business.id.toString(),
        businessname: business.businessname,
        businessimageurl:
          business.businessimageurl || "/placeholder.svg?key=4vuv3",
        businessaddress: business.businessaddress1,
      };

      services.forEach((service) => {
        if (!result[service]) {
          result[service] = [];
        }
        result[service].push(vendor);
      });
    });

    setVendors(result);
  }, [businessData]);

  /*
  const toggleVendor = (category, vendorName) => {
    const currentVendors = formData.vendors[category] || [];
    const updatedVendors = currentVendors.includes(vendorName)
      ? currentVendors.filter(v => v !== vendorName)
      : [...currentVendors, vendorName];

    updateFormData({
      vendors: {
        ...formData.vendors,
        [category]: updatedVendors
      }
    });
  };

  const isVendorSelected = (category, vendorName) => {
    return (formData.vendors[category] || []).includes(vendorName);
  };
  */
  const toggleVendor = (vendorId) => {
    const currentVendors = formData.vendors || [];
    const updatedVendors = currentVendors.includes(vendorId)
      ? currentVendors.filter((id) => id !== vendorId)
      : [...currentVendors, vendorId];

    updateFormData({
      vendors: updatedVendors,
    });
  };

  const isVendorSelected = (vendorId) => {
    return (formData.vendors || []).includes(vendorId);
  };

  const toggleNeed = (need) => {
    const updatedNeeds = formData.needs.includes(need)
      ? formData.needs.filter((n) => n !== need)
      : [...formData.needs, need];

    updateFormData({ needs: updatedNeeds });
  };

  const isNeedSelected = (need) => formData.needs.includes(need);

  const handleAddNeeds = () => {
    setShowAddNeeds(false);
    if (formData.needs.length > 0 && !activeNeed) {
      setActiveNeed(formData.needs[0]);
    }
  };

  return (
    <div>
      <StepMarker currentStep={4} totalSteps={5} onBack={onBack} />

      <h2 className="text-xl font-bold mb-1">
        No middlemen—partners connect directly!
      </h2>
      <p className="text-gray-500 mb-4">
        Trusted {activeNeed.toLowerCase()} partners
      </p>

      <div className="mb-6">
        <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">
          Selected event needs
        </h3>
        <div className="flex flex-wrap gap-2">
          {selectedNeeds.map((need) => (
            <button
              key={need}
              onClick={() => setActiveNeed(need)}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                activeNeed === need
                  ? "bg-[#7B189F] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {need}
            </button>
          ))}
          <button
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"
            onClick={() => setShowAddNeeds(true)}
          >
            <Plus size={16} />
          </button>
        </div>
      </div>

      {/* Add More Needs Modal */}
      {showAddNeeds && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Add More Event Needs</h3>
              <button
                onClick={() => setShowAddNeeds(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {eventNeeds.map((need) => (
                <div
                  key={need.id}
                  onClick={() => toggleNeed(need.eventneedsname)}
                  className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                    isNeedSelected(need.eventneedsname)
                      ? "border-green-500 bg-green-50"
                      : "hover:border-[#7B189F]"
                  }`}
                >
                  <div className="flex items-center">
                    <span className="mr-2">{need.image_url}</span>
                    <div>
                      <h3 className="font-medium">{need.eventneedsname}</h3>
                      <p className="text-sm text-gray-500" />
                    </div>
                  </div>
                  <div
                    className={`w-6 h-6 flex items-center justify-center rounded-sm border ${
                      isNeedSelected(need.eventneedsname)
                        ? "bg-green-500 border-green-500"
                        : "border-gray-300"
                    }`}
                  >
                    {isNeedSelected(need.eventneedsname) && (
                      <Check size={16} className="text-white" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleAddNeeds}
                className="bg-[#7B189F] hover:bg-[#671485] text-white"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {selectedNeeds.length > 0 && activeNeed && (
        <div>
          <h3 className="text-sm font-medium mb-4">
            {vendors && (vendors[activeNeed]?.length ?? 0)} Popular{" "}
            {activeNeed.toLowerCase()} found near you
          </h3>
          <div className="space-y-3">
            {vendors && (vendors[activeNeed]?.length ?? 0) === 0 && (
              <div>
                <p className="text-gray-500 mb-6">
                  Sorry No Business Found For Your Needs.
                </p>
              </div>
            )}

            {vendors &&
              (vendors?.[activeNeed] ?? []).map((vendor) => {
                return (
                  <div
                    key={vendor.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <img
                          src={vendor.businessimageurl || "/placeholder.svg"}
                          alt={vendor.businessname}
                          className="w-12 h-12 mr-4 rounded-full"
                        />
                        <div>
                          <h4 className="font-medium">{vendor.businessname}</h4>

                          <p className="text-sm text-gray-500">
                            Address: {vendor.businessaddress}
                          </p>
                          <div className="flex items-center mt-1 text-xs">
                            {false && (
                              <div className="flex items-center mr-4">
                                <MapPin
                                  size={12}
                                  className="text-gray-500 mr-1"
                                />
                                <span>{vendor.distance}</span>
                              </div>
                            )}
                            {false && vendor.freeDelivery && (
                              <div className="flex items-center">
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="mr-1"
                                >
                                  <path
                                    d="M11.6673 7.58333V5.25C11.6673 5.01 11.5723 4.78 11.4023 4.61L9.33398 2.54167C9.16398 2.37167 8.93398 2.27667 8.69398 2.27667H3.50065C2.85898 2.27667 2.33398 2.80167 2.33398 3.44333V7.58333C2.33398 8.225 2.85898 8.75 3.50065 8.75H10.5007C11.1423 8.75 11.6673 8.225 11.6673 7.58333Z"
                                    stroke="#22C55E"
                                    strokeWidth="1.2"
                                  />
                                  <path
                                    d="M11.6673 7.58333H10.5007C9.85898 7.58333 9.33398 7.05833 9.33398 6.41667V3.44333H8.16732"
                                    stroke="#22C55E"
                                    strokeWidth="1.2"
                                  />
                                  <path
                                    d="M3.5 11.0833C4.14433 11.0833 4.66667 10.561 4.66667 9.91667C4.66667 9.27233 4.14433 8.75 3.5 8.75C2.85567 8.75 2.33333 9.27233 2.33333 9.91667C2.33333 10.561 2.85567 11.0833 3.5 11.0833Z"
                                    stroke="#22C55E"
                                    strokeWidth="1.2"
                                  />
                                  <path
                                    d="M10.5 11.0833C11.1443 11.0833 11.6667 10.561 11.6667 9.91667C11.6667 9.27233 11.1443 8.75 10.5 8.75C9.85567 8.75 9.33333 9.27233 9.33333 9.91667C9.33333 10.561 9.85567 11.0833 10.5 11.0833Z"
                                    stroke="#22C55E"
                                    strokeWidth="1.2"
                                  />
                                </svg>
                                <span className="text-green-500">
                                  Free Delivery
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div
                        onClick={() => toggleVendor(vendor.id)}
                        className={`w-6 h-6 flex items-center justify-center rounded-sm border cursor-pointer ${
                          isVendorSelected(vendor.id)
                            ? "bg-gray-200 border-gray-300"
                            : "border-gray-300"
                        }`}
                      >
                        {isVendorSelected(vendor.id) && (
                          <Check size={16} className="text-gray-600" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {selectedNeeds.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No event needs selected. Go back to select your event needs.</p>
        </div>
      )}

      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <div className="space-x-3">
          <Button variant="outline" onClick={onSkip}>
            Skip
          </Button>
          <Button
            onClick={onNext}
            className="bg-[#7B189F] hover:bg-[#671485] text-white"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
